import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Save, Plus, Minus, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { EditableText, AutoGrow, Collapsible } from '../../components/projects/PersonalProfileEditor';
import { Link } from 'react-router-dom';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const FUNNEL = ['awareness', 'consideration', 'conversion'];

// Bereich der Strategie-Ansicht: zugeklappt nur Titel + Zusammenfassung.
const Section = ({ title, summary, hint, children, action, defaultOpen = false, testId }) => (
  <div className="mb-3">
    <Collapsible title={title} summary={summary} defaultOpen={defaultOpen} right={action} testId={testId}>
      {hint && <p className="text-xs text-[#8696A9] mb-3">{hint}</p>}
      {children}
    </Collapsible>
  </div>
);

/**
 * Strategie-Ansicht (Marke → Strategie).
 *  - Unternehmen: Strategie aus Brand DNA, bearbeitbar.
 *  - Person: Strategie je Rolle bzw. kombiniert; das Profil selbst (Werdegang, Rollen, Lebenslauf, Stimme) liegt in der
 *    Personal DNA. Bei Import aus einem Firmen-Projekt der Hinweis „Firma wurde geändert — übernehmen?".
 * Die Strategie steuert die Ideen in der Kreation.
 */
const StrategyView = () => {
  const { getAuthHeader } = useAuth();
  const { lang } = useLanguage();
  const T = (de, en) => (lang === 'de' ? de : en);
  const headers = getAuthHeader();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [strategy, setStrategy] = useState(null);       // bearbeitbare Kopie
  const [strategyDirty, setStrategyDirty] = useState(false);
  const [scope, setScope] = useState('combined');     // 'combined' oder Rollen-ID (nur bei mehreren parallelen Rollen)
  const [busy, setBusy] = useState('');                  // 'save-strategy' | 'generate' | 'sync' | 'dismiss'

  const roleQs = (sc) => (sc && sc !== 'combined' ? `?role=${encodeURIComponent(sc)}` : '');

  const load = useCallback(async (sc = 'combined') => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/projects/active/strategy${roleQs(sc)}`, { headers: getAuthHeader() });
      setData(d);
      setStrategy(d.strategy ? JSON.parse(JSON.stringify(d.strategy)) : null);
      setStrategyDirty(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || T('Strategie konnte nicht geladen werden', 'Could not load strategy'));
    } finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const switchScope = (sc) => {
    if (sc === scope) return;
    if (strategyDirty && !window.confirm(T('Ungespeicherte Änderungen an dieser Strategie gehen verloren. Fortfahren?', 'Unsaved changes to this strategy will be lost. Continue?'))) return;
    setScope(sc);
    load(sc);
  };

  const isPerson = data?.project_type === 'person';
  const err = (e, fallback) => toast.error(e.response?.data?.detail || fallback);

  const updStrategy = (patch) => { setStrategy(s => ({ ...(s || { pillars: [] }), ...patch })); setStrategyDirty(true); };
  const updPillar = (i, patch) => updStrategy({ pillars: strategy.pillars.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) });

  const saveStrategy = async () => {
    setBusy('save-strategy');
    try {
      const { data: r } = await axios.put(`${API}/projects/active/strategy${roleQs(scope)}`, { strategy }, { headers });
      setStrategy(r.strategy); setStrategyDirty(false);
      toast.success(T('Strategie gespeichert', 'Strategy saved'));
    } catch (e) { err(e, T('Speichern fehlgeschlagen', 'Saving failed')); } finally { setBusy(''); }
  };

  const generate = async () => {
    if (strategy?.user_edited && !window.confirm(T('Deine bearbeitete Strategie wird ersetzt. Fortfahren?', 'Your edited strategy will be replaced. Continue?'))) return;
    setBusy('generate');
    try {
      const { data: r } = await axios.post(`${API}/projects/active/strategy/generate${roleQs(scope)}`, {}, { headers, timeout: 120000 });
      setStrategy(r.strategy); setStrategyDirty(false);
      setData(d => ({ ...d, strategy: r.strategy }));
      toast.success(T('Strategie erstellt', 'Strategy created'));
    } catch (e) { err(e, T('Strategie konnte nicht erstellt werden', 'Could not create strategy')); } finally { setBusy(''); }
  };

  const syncAction = async (kind) => {
    setBusy(kind === 'apply' ? 'sync' : 'dismiss');
    try {
      await axios.post(`${API}/projects/active/import-sync/${kind}`, {}, { headers });
      if (kind === 'apply') toast.success(T('Änderungen übernommen', 'Changes applied'));
      await load(scope);
    } catch (e) { err(e, T('Aktion fehlgeschlagen', 'Action failed')); } finally { setBusy(''); }
  };

  if (loading) {
    return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00D9C8] mx-auto" /></div>;
  }
  if (!data) return null;

  const imp = data.import;
  const GROUP_LABEL = {
    brand_dna: T('Brand DNA', 'Brand DNA'),
    manuals: T('Plattform-Manuals', 'Platform manuals'),
    company: T('Firmenprofil', 'Company profile'),
  };
  const stageLabel = { awareness: T('Aufmerksamkeit', 'Awareness'), consideration: T('Abwägung', 'Consideration'), conversion: T('Entscheidung', 'Conversion') };
  const mix = strategy?.funnel_mix || {};

  return (
    <div data-testid="strategy-view">
      <div className="mb-4">
        <h2 className="text-white text-lg font-semibold">{T('Strategie', 'Strategy')}</h2>
        <p className="text-sm text-[#A0AEC0]">
          {isPerson
            ? T('Worüber du schreibst und wie. Die Ideen in der Kreation bauen darauf auf.', 'What you write about and how. The ideas in Create build on this.')
            : T('Ziel, Zielgruppe und Content-Säulen deiner Marke. Die Ideen in der Kreation bauen darauf auf.', 'Goal, audience and content pillars of your brand. The ideas in Create build on this.')}
        </p>
      </div>

      {/* Mehrere parallele Rollen: je Rolle eigene Strategie, dazu die kombinierte */}
      {isPerson && (data.roles || []).length >= 2 && (
        <div className="mb-4" data-testid="role-tabs">
          <div className="flex flex-wrap gap-2">
            {[{ id: 'combined', label: T('Kombiniert', 'Combined') }, ...data.roles.map(r => ({ id: r.id, label: [r.job, r.company].filter(Boolean).join(' @ ') }))].map(t => (
              <button type="button" key={t.id} onClick={() => switchScope(t.id)} className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${scope === t.id ? 'border-[#00D9C8] bg-[#00D9C8]/15 text-white' : 'border-[#2A3B52] text-[#A0AEC0] hover:text-white hover:border-[#00D9C8]/40'}`} data-testid={`role-tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-[#6B7C90] mt-2">
            {scope === 'combined'
              ? T('Beiträge, die deine Rollen verbinden. Jede Rolle hat zusätzlich eine eigene Strategie.', 'Posts that connect your roles. Each role also has its own strategy.')
              : T('Eigene Strategie nur für diese Rolle. Die Ideen daraus erwähnen deine anderen Rollen nicht.', 'A separate strategy for this role only. Ideas from it do not mention your other roles.')}
          </p>
        </div>
      )}

      {/* Firma wurde geändert */}
      {imp?.pending && (
        <div className="mb-4 rounded-xl border border-[#F5A524]/40 bg-[#F5A524]/10 p-4" data-testid="sync-banner">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-[#F5A524] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">
                {T(`„${imp.source_name}“ wurde geändert. Übernehmen?`, `"${imp.source_name}" has changed. Apply the changes?`)}
              </p>
              <p className="text-xs text-[#A0AEC0] mt-1">
                {T('Geändert: ', 'Changed: ')}{imp.changed.map(g => GROUP_LABEL[g]).join(', ')}.{' '}
                {T('Beim Übernehmen werden deine eigenen Änderungen an diesen Bereichen ersetzt. Dein Profil und deine Strategie bleiben erhalten.',
                   'Applying replaces your own edits in these areas. Your profile and strategy are kept.')}
              </p>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={() => syncAction('apply')} disabled={!!busy} className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="sync-apply">
                  {busy === 'sync' && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}{T('Übernehmen', 'Apply')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => syncAction('dismiss')} disabled={!!busy} className="text-[#A0AEC0] hover:text-white" data-testid="sync-dismiss">
                  {T('Ignorieren', 'Ignore')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {imp?.source_missing && (
        <p className="mb-4 text-xs text-[#5A6C7D]" data-testid="sync-missing">{T('Das Projekt, aus dem importiert wurde, existiert nicht mehr. Der Abgleich ist deaktiviert.', 'The project this was imported from no longer exists. Syncing is disabled.')}</p>
      )}

      {/* Das Profil liegt in der Personal DNA */}
      {isPerson && (
        <div className="mb-3 rounded-xl border border-[#1A2B42] bg-[#0F1D32] p-3.5 flex items-center justify-between gap-3" data-testid="personal-dna-pointer">
          <p className="text-xs text-[#A0AEC0]">{T('Profil, Werdegang, Rollen, Lebenslauf und Stimme bearbeitest du in deiner Personal DNA. Die Strategie baut darauf auf.', 'You edit your profile, career, roles, CV and voice in your Personal DNA. The strategy builds on it.')}</p>
          <Link to="/app/einstellungen?tab=marke&sub=uebersicht" className="shrink-0 text-sm text-[#00D9C8] hover:underline" data-testid="personal-dna-link">{T('Personal DNA öffnen', 'Open Personal DNA')}</Link>
        </div>
      )}

      {/* Strategie */}
      <Section
        testId="section-strategy"
        title={T('Content-Strategie', 'Content strategy')}
        summary={strategy ? `${(strategy.pillars || []).length} ${T('Säulen', 'pillars')}${strategy.goal ? ' · ' + strategy.goal : ''}` : T('Noch nicht erstellt', 'Not created yet')}
        defaultOpen={!strategy}
        hint={strategy?.user_edited ? T('Von dir bearbeitet — wird nicht automatisch überschrieben.', 'Edited by you — never overwritten automatically.') : T('Von der KI aus deinen Daten abgeleitet. Mit dem Stift anpassbar.', 'Derived by the AI from your data. Adjust with the pencil.')}
        action={(
          <div className="flex gap-2">
            {strategyDirty && (
              <Button size="sm" onClick={saveStrategy} disabled={!!busy} className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="strategy-save">
                {busy === 'save-strategy' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}{T('Speichern', 'Save')}
              </Button>
            )}
            {strategy && (
              <Button size="sm" variant="ghost" onClick={generate} disabled={!!busy} className="text-[#A0AEC0] hover:text-white" data-testid="strategy-regenerate">
                {busy === 'generate' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}{T('Neu erstellen', 'Regenerate')}
              </Button>
            )}
          </div>
        )}
      >
        {!strategy ? (
          <div className="text-center py-8" data-testid="strategy-empty">
            <Sparkles className="w-7 h-7 text-[#00D9C8] mx-auto mb-3" />
            <p className="text-sm text-[#A0AEC0] mb-4">{T('Noch keine Strategie. Sie entsteht automatisch mit den ersten Ideen oder jetzt auf Knopfdruck.', 'No strategy yet. It is created automatically with your first ideas, or right now on demand.')}</p>
            <Button onClick={generate} disabled={!!busy} className="bg-[#00D9C8] hover:bg-[#00C4B4] text-[#0A1628]" data-testid="strategy-generate">
              {busy === 'generate' && <Loader2 className="w-4 h-4 animate-spin mr-2" />}{T('Strategie erstellen', 'Create strategy')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <EditableText label={T('Ziel', 'Goal')} value={strategy.goal} onChange={v => updStrategy({ goal: v })} testId="strategy-goal" />
            <EditableText label={T('Zielgruppe', 'Audience')} value={strategy.audience} onChange={v => updStrategy({ audience: v })} testId="strategy-audience" />
            <EditableText label={T('Was nur ihr sagen könnt', 'What only you can say')} value={strategy.differentiator} onChange={v => updStrategy({ differentiator: v })} testId="strategy-differentiator" />

            <div data-testid="strategy-pillars">
              <div className="flex items-center justify-between mb-2 px-0.5">
                <p className="text-[11px] uppercase tracking-wide text-[#6B7C90]">{T('Content-Säulen', 'Content pillars')}</p>
                {(strategy.pillars || []).length < 6 && (
                  <button type="button" onClick={() => updStrategy({ pillars: [...(strategy.pillars || []), { name: '', description: '', audience_pain: '', angles: [], funnel_stage: 'awareness' }] })} className="text-[#00D9C8]" data-testid="pillar-add"><Plus className="w-4 h-4" /></button>
                )}
              </div>
              <div className="space-y-2">
                {(strategy.pillars || []).map((p, i) => (
                  <Collapsible
                    key={i} tone="inner" testId={`pillar-${i}`} defaultOpen={!p.name}
                    title={p.name || T('Neue Säule', 'New pillar')}
                    summary={stageLabel[p.funnel_stage || 'awareness']}
                    right={<button type="button" onClick={() => updStrategy({ pillars: strategy.pillars.filter((_, idx) => idx !== i) })} className="text-[#5A6C7D] hover:text-red-400 p-1" aria-label="Remove" data-testid={`pillar-remove-${i}`}><Minus className="w-4 h-4" /></button>}
                  >
                    <div className="space-y-2">
                      <AutoGrow value={p.name} onChange={e => updPillar(i, { name: e.target.value })} placeholder={T('Name der Säule', 'Pillar name')} className="bg-[#0A1628] border-[#2A3B52] text-white text-sm font-medium" data-testid={`pillar-name-${i}`} />
                      <AutoGrow value={p.description} onChange={e => updPillar(i, { description: e.target.value })} placeholder={T('Worum geht es?', 'What is it about?')} className="bg-[#0A1628] border-[#2A3B52] text-white text-sm" />
                      <AutoGrow value={p.audience_pain} onChange={e => updPillar(i, { audience_pain: e.target.value })} placeholder={T('Welches Problem der Zielgruppe löst es?', 'Which audience problem does it answer?')} className="bg-[#0A1628] border-[#2A3B52] text-[#A0AEC0] text-xs" />
                      <AutoGrow value={(p.angles || []).join('\n')} onChange={e => updPillar(i, { angles: e.target.value.split('\n') })} placeholder={T('Blickwinkel (einer pro Zeile)', 'Angles (one per line)')} className="bg-[#0A1628] border-[#2A3B52] text-[#A0AEC0] text-xs" />
                      <select style={{ colorScheme: 'dark' }} value={p.funnel_stage || 'awareness'} onChange={e => updPillar(i, { funnel_stage: e.target.value })} className="rounded-md bg-[#0A1628] border border-[#2A3B52] text-white text-xs px-2 py-1.5">
                        {FUNNEL.map(f => <option key={f} value={f}>{stageLabel[f]}</option>)}
                      </select>
                    </div>
                  </Collapsible>
                ))}
              </div>
            </div>

            <div className="bg-[#1A2B42] rounded-lg p-3" data-testid="strategy-mix">
              <p className="text-xs uppercase tracking-wide text-[#5A6C7D] mb-2">{T('Mix (Prozent)', 'Mix (percent)')}</p>
              <div className="grid grid-cols-3 gap-2">
                {FUNNEL.map(f => (
                  <label key={f} className="text-xs text-[#A0AEC0]">
                    {stageLabel[f]}
                    <Input type="number" min={0} max={100} value={mix[f] ?? 0} onChange={e => updStrategy({ funnel_mix: { ...mix, [f]: Number(e.target.value) } })} className="mt-1 bg-[#0A1628] border-[#2A3B52] text-white h-8" data-testid={`mix-${f}`} />
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
};

export default StrategyView;
